import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  buildShiftStartAt,
  extractAttendanceMeta,
  getNoShowBannerState,
} from "./attendanceHelpers";

/**
 * Tracks the 1h–2h no-show decision window and schedules attendance refetch
 * at the 1-hour mark and when entering the action phase.
 */
export function useNoShowAttendanceWindow({
  enabled,
  activeDate,
  bookingTimezone,
  attendanceRes,
  responseReady,
  refetch,
}) {
  const [now, setNow] = useState(() => dayjs());
  const oneHourRefetchScheduledRef = useRef(false);
  const actionRefetchDoneRef = useRef(false);
  const prevPhaseRef = useRef(null);

  const meta = useMemo(() => {
    if (!responseReady || !attendanceRes) return null;
    return extractAttendanceMeta(attendanceRes);
  }, [attendanceRes, responseReady]);

  const shiftStartAt = useMemo(() => {
    if (!activeDate || !meta?.shiftStartTime) return null;
    return buildShiftStartAt(activeDate, meta.shiftStartTime, bookingTimezone);
  }, [activeDate, meta?.shiftStartTime, bookingTimezone]);

  const bannerState = useMemo(() => {
    if (!enabled || !meta) {
      return {
        phase: "before",
        showBanner: false,
        msUntilOneHour: null,
        msUntilTwoHours: null,
      };
    }
    return getNoShowBannerState({ shiftStartAt, now, meta });
  }, [enabled, shiftStartAt, now, meta]);

  useEffect(() => {
    oneHourRefetchScheduledRef.current = false;
    actionRefetchDoneRef.current = false;
    prevPhaseRef.current = null;
  }, [activeDate, meta?.shiftStartTime]);

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(dayjs()), 1000);
    return () => clearInterval(id);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !refetch || !shiftStartAt?.isValid()) return;

    const { phase, msUntilOneHour } = bannerState;

    if (
      phase === "before" &&
      msUntilOneHour != null &&
      msUntilOneHour > 0 &&
      !oneHourRefetchScheduledRef.current
    ) {
      oneHourRefetchScheduledRef.current = true;
      const timeoutId = setTimeout(() => {
        refetch();
      }, msUntilOneHour);
      return () => clearTimeout(timeoutId);
    }
  }, [enabled, refetch, shiftStartAt, bannerState]);

  useEffect(() => {
    if (!enabled || !refetch) return;

    const { phase } = bannerState;
    const prevPhase = prevPhaseRef.current;
    prevPhaseRef.current = phase;

    if (
      phase === "action" &&
      prevPhase !== "action" &&
      !actionRefetchDoneRef.current
    ) {
      actionRefetchDoneRef.current = true;
      refetch();
    }
  }, [enabled, refetch, bannerState]);

  return { meta, bannerState };
}
